import React, { useState, useEffect, useContext, useRef } from "react";
import Big from "big.js";
import { connect } from "react-redux";
import moment from "moment";
import queryString from "query-string";
import { classNames } from "@features/utils/reactFunctions";
import BttfAxios from "@features/utils/axios";
import { withRouter } from "react-router";
import { queryPage } from "@features/utils/functions";
import DownloadIcon from "@assets/svg/download.svg";
import ExportIcon from "@assets/svg/export.svg";

import TransactionHistoryTable from "@funds/components/TransactionHistoryTable/TransactionHistoryTable";
import TransactionHistoryNav from "@funds/components/TransactionHistoryNav/TransactionHistoryNav";

import CsvGenerator from "@appComponents/CsvGenerator/CsvGenerator";
import JSONGenerator from "@appComponents/JSONGenerator/JSONGenerator";
import ToolTip from "@appComponents/ToolTip/ToolTip";

import FundsService from "@services/Funds.service";

import { TReducers } from "@features/reducers";
import ITransaction from "@funds/interfaces/ITransaction";
import ITransactionHistory, {
  TTransactionsHistory,
} from "./ITransactionHistory";

import { FundsAppContext } from "@features/context";
import recapSvg from "@images/recapio.svg?url";

import * as style from "./TransactionHistoryStyle.module.scss";
import { useMediaQuery } from "react-responsive";
import { BottomSheet } from "@appComponents/BottomSheet";

const TransactionHistory: React.FunctionComponent<ITransactionHistory> = (
  props
) => {
  const { investorId } = useContext(FundsAppContext);
  const [isDownloadSelectOpen, setIsDownloadSelectOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [historyState, setHistoryState] = useState<TTransactionsHistory>({
    transactions: [],
    paginationConfig: {
      currentPage: 1,
      totalCount: 0,
      perPage: 10,
    },
    isLoading: false,
    balanceBefore: null,
    currency: undefined,
    dateFilters: {
      fromDate: undefined,
      toDate: undefined,
    },
  });

  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      const query = queryString.parse(props.location.search);
      Object.keys(query).length
        ? setHistoryState((prevState) => {
            return {
              ...prevState,
              paginationConfig: {
                ...prevState.paginationConfig,
                currentPage: queryPage(props.location.search),
              },
              currency: query.currency,
              dateFilters: {
                fromDate: query.date_from
                  ? new Date(query.date_from)
                  : prevState.dateFilters.fromDate,
                toDate: query.date_to
                  ? new Date(query.date_to)
                  : prevState.dateFilters.toDate,
              },
            };
          })
        : fetchTransactions();
      return;
    }

    const page = historyState.paginationConfig.currentPage;
    const currency = historyState.currency;
    const dateFrom = historyState.dateFilters.fromDate
      ? moment(historyState.dateFilters.fromDate).format("YYYY-MM-DD")
      : undefined;
    const dateTo = historyState.dateFilters.toDate
      ? moment(historyState.dateFilters.toDate).format("YYYY-MM-DD")
      : undefined;
    const params = BttfAxios.paramBuilder({
      currency,
      page,
      date_from: dateFrom,
      date_to: dateTo,
    });

    params && props.history.push(params);

    fetchTransactions();
  }, [
    historyState.currency,
    historyState.dateFilters,
    historyState.paginationConfig.currentPage,
  ]);

  const addBalance = (transactions: ITransaction[], balanceBefore: string) => {
    return transactions
      .reverse()
      .map((el: ITransaction, i: number, arr: ITransaction[]) => {
        return {
          ...el,
          balance: Number(
            Big(balanceBefore).add(
              arr.slice(0, i + 1).reduce((acc, curr) => {
                return Number(
                  Big(acc)
                    .plus(curr.in || 0)
                    .minus(curr.out || 0)
                );
              }, 0)
            )
          ),
        };
      })
      .reverse();
  };

  const resetPagination = { currentPage: 1, totalCount: 0, perPage: 10 };

  const changePage = (page: number) => {
    setHistoryState((prevState) => {
      return {
        ...prevState,
        paginationConfig: {
          ...prevState.paginationConfig,
          currentPage: page,
        },
      };
    });
  };

  const clearFilters = () =>
    setHistoryState((prevState) => {
      return {
        ...prevState,
        dateFilters: { fromDate: undefined, toDate: undefined },
        currency: undefined,
      };
    });

  const updateCurrency = (currency: string) =>
    setHistoryState((prevState) => {
      return {
        ...prevState,
        paginationConfig: resetPagination,
        currency: currency || undefined,
      };
    });

  const updateDates = ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }) => {
    setHistoryState((prevState: TTransactionsHistory) => {
      return {
        ...prevState,
        dateFilters: { fromDate: startDate, toDate: endDate },
        paginationConfig: resetPagination,
      };
    });
  };

  const fetchTransactions = (
    page: number | undefined = historyState.paginationConfig.currentPage
  ) => {
    if (investorId) {
      setHistoryState((prevState: TTransactionsHistory) => {
        return { ...prevState, isLoading: true };
      });
      FundsService.getTransactionsHistory(
        investorId,
        page,
        historyState.currency,
        historyState.dateFilters.fromDate
          ? moment(historyState.dateFilters.fromDate).format("YYYY-MM-DD")
          : undefined,
        historyState.dateFilters.toDate
          ? moment(historyState.dateFilters.toDate).format("YYYY-MM-DD")
          : undefined
      ).then((res) => {
        const balanceBefore = res.data.balance_before;
        setHistoryState((prevState: TTransactionsHistory) => {
          return {
            ...prevState,
            transactions:
              balanceBefore !== null
                ? addBalance(res.data.items, balanceBefore)
                : res.data.items,
            paginationConfig: {
              currentPage: res.data.page,
              perPage: res.data.per_page,
              totalCount: res.data.total_count,
            },
            balanceBefore: balanceBefore,
            isLoading: false,
          };
        });
      });
    }
  };

  return (
    <div className={style.transactionHistory}>
      <div className={style.headerWrapper}>
        <h2 className={style.headline}>Transaction History</h2>
        {!isMobile && (
          <div className={style.actionsWrapper}>
            <div className={style.recapWrapper}>
              <JSONGenerator
                label={
                  <>
                    <ToolTip
                      text={
                        <span>
                          BnkToTheFuture has partnered with Recap so that you
                          can import this file in order to generate your Crypto
                          tax reports in an instant.
                        </span>
                      }
                    >
                      <span>
                        Export for{" "}
                        <img src={recapSvg} className={style.recap} />
                      </span>
                    </ToolTip>
                  </>
                }
                fileName="transaction-history-recap"
                baseEndpoint={`/api/funds/investors/${investorId}/transactions?per_page=10000`}
              />
              <a
                href="http://recap.io/"
                target="_blank"
                className={style.recapLink}
              >
                Set-up an account on Recap
              </a>
            </div>
            <CsvGenerator
              baseEndpoint={`/api/funds/investors/${investorId}/transactions/`}
              endpointDetails={{
                currency: historyState.currency,
                date_from:
                  historyState.dateFilters.fromDate &&
                  moment(historyState.dateFilters.fromDate).format(
                    "YYYY-MM-DD"
                  ),
                date_to:
                  historyState.dateFilters.toDate &&
                  moment(historyState.dateFilters.toDate).format("YYYY-MM-DD"),
              }}
              fileName="transaction-history"
            />

            {isMobile && (
              <div onClick={() => setIsDownloadSelectOpen(true)}>
                Open download select
              </div>
            )}
          </div>
        )}
      </div>
      <TransactionHistoryNav
        currency={historyState.currency || ""}
        updateCurrency={updateCurrency}
        wallets={props.allUserWallets}
        updateState={updateDates}
        startDate={historyState.dateFilters.fromDate}
        endDate={historyState.dateFilters.toDate}
        clearFilters={clearFilters}
        handleOpenDownloadModal={() => setIsDownloadSelectOpen(true)}
      />
      <div className={classNames(historyState.isLoading && style.loading)}>
        <TransactionHistoryTable
          transactions={historyState.transactions}
          balance={historyState.balanceBefore}
          filterData={changePage}
          paginationConfig={historyState.paginationConfig}
        />
      </div>

      {isMobile && (
        <BottomSheet
          title="Download"
          open={isDownloadSelectOpen}
          onClose={() => setIsDownloadSelectOpen(false)}
        >
          <div className="flex flex-col gap-1 py-8">
            <CsvGenerator
              buttomClassname="p-[15px] border border-grey-lighter bg-[#F6F6F6] rounded-[10px] font-semibold flex items-center gap-4"
              baseEndpoint={`/api/funds/investors/${investorId}/transactions/`}
              endpointDetails={{
                currency: historyState.currency,
                date_from:
                  historyState.dateFilters.fromDate &&
                  moment(historyState.dateFilters.fromDate).format(
                    "YYYY-MM-DD"
                  ),
                date_to:
                  historyState.dateFilters.toDate &&
                  moment(historyState.dateFilters.toDate).format("YYYY-MM-DD"),
              }}
              fileName="transaction-history"
              customIcon={<DownloadIcon />}
              customIconPosition="left"
            />

            <JSONGenerator
              label="Export for Recap.io"
              fileName="transaction-history-recap"
              baseEndpoint={`/api/funds/investors/${investorId}/transactions?per_page=10000`}
              buttonClassname="p-[15px] border border-grey-lighter bg-[#F6F6F6] rounded-[10px] font-semibold flex items-center gap-4"
              customIcon={<ExportIcon />}
            />
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  allUserWallets: store.fundsReducer.allUserWallets,
});

export default withRouter(connect(mapStateToProps)(TransactionHistory));
